// Videos de Escuela Vial (de 30 s a ~1 min): uno por tema del Libro para la Conducción en Chile (Clase B, CONASET).
// Se producen en C:\Users\E14\OpenMontage\remotion-composer y se sirven desde Cloudinary en
// escuela-vial/video/<archivo>-<horizontal|vertical>.mp4 (horizontal para la web, vertical para app y Shorts).
// Cada tema sale de una pregunta del banco que se falla mucho; `error` es el % real de respuestas incorrectas.

const CLOUD = "https://res.cloudinary.com/dr2vey1jk/video/upload";

export type Video = {
  slug: string;
  /** Nombre del archivo en Cloudinary (sin formato ni extensión). */
  archivo: string;
  /** H1 y título del video: formulado como la búsqueda que responde. */
  titulo: string;
  descripcion: string;
  segundos: number;
  /** Lo que dice la voz, escena por escena (con las cifras en dígitos). */
  transcripcion: string[];
  capitulo: { nombre: string; ruta: string };
  pregunta: { q: string; a: string; error: number };
  fecha: string;
};

export const VIDEOS_LASTMOD = "2026-10-30";

export const VIDEOS: Video[] = [
  // Videos de ~1 minuto (solo horizontal): temas que piden más explicación.
  {
    slug: "distancia-de-detencion",
    archivo: "detencion",
    titulo: "¿Cuántos metros necesitas para detener tu auto?",
    descripcion:
      "Distancia de reacción (1 segundo, unos 25 m a 90 km/h) más distancia de frenado, que crece al cuadrado. Lluvia ×2, hielo ×10, y cómo calcular la distancia con el de adelante.",
    segundos: 75,
    transcripcion: [
      "¿Sabes cuántos metros necesitas para detener tu auto? La respuesta tiene dos partes.",
      "Primero, la distancia de reacción: lo que avanzas mientras reaccionas, normalmente un segundo. A 90 km/h, eso ya son unos 25 metros.",
      "Un truco del libro: multiplica la primera cifra de la velocidad por 3. A 50 km/h, 5 × 3: unos 15 metros.",
      "Después viene la distancia de frenado, desde que pisas el freno hasta detenerte. Y crece al cuadrado: si duplicas la velocidad, se multiplica por 4; si la triplicas, por 9.",
      "El pavimento también cuenta: con lluvia, la distancia de frenado es al menos el doble, y con hielo puede ser hasta 10 veces mayor.",
      "Por eso importa la distancia con el auto de adelante. En carretera, deja tantos metros como km/h marca tu velocímetro: a 80, unos 80 metros. En la ciudad, puede ser la mitad.",
      "Para comprobarla, usa la regla de los 3 segundos: cuando el auto de adelante pase un punto fijo, cuenta mil uno, mil dos, mil tres. Si llegas antes, vas demasiado cerca.",
      "Casi cuatro de cada diez fallan estas preguntas. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Los principios de la conducción", ruta: "/materiales/principios-de-la-conduccion" },
    pregunta: {
      q: "¿Cuántos metros recorre aproximadamente un vehículo en un segundo de reacción a 50 km/h?",
      a: "Unos 15 metros: se multiplica por tres la primera cifra de la velocidad (5 × 3).",
      error: 38,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "frenada-de-emergencia-abs",
    archivo: "frenos",
    titulo: "¿Cómo frenar en una emergencia, con y sin ABS?",
    descripcion:
      "Con ABS, pisa a fondo y mantén la presión: puedes esquivar mientras frenas, aunque no siempre frenas en menos metros. Sin ABS, pisa y afloja si las ruedas se bloquean.",
    segundos: 60,
    transcripcion: [
      "Frenada de emergencia: ¿pisas a fondo o vas soltando el pedal? Depende de si tu auto tiene ABS.",
      "ABS significa sistema de frenado antibloqueo. Evita que las ruedas se bloqueen cuando frenas fuerte.",
      "Con ABS, pisa el freno a fondo y mantén la presión hasta el final. Sentirás un leve temblor en el pedal: es normal.",
      "Su gran ventaja es que puedes seguir girando el volante para esquivar mientras frenas.",
      "Pero ojo: con ABS no siempre frenas en menos metros. En algunas emergencias, la distancia de frenado incluso puede ser mayor.",
      "Sin ABS, pisa enérgicamente. Si las ruedas se bloquean, reduce un poco la presión hasta que dejen de patinar y recuperen la adherencia.",
      "Y si los frenos fallan por completo: bombea el pedal, baja de marcha para frenar con el motor y usa el freno de mano de forma suave y progresiva. Nunca apagues el motor.",
      "Casi cuatro de cada diez fallan estas preguntas. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Informaciones importantes", ruta: "/materiales/informaciones-importantes" },
    pregunta: {
      q: "¿Cómo se debe frenar correctamente en un vehículo SIN sistema ABS ante una frenada de emergencia?",
      a: "Pisando enérgicamente el freno y, si las ruedas se bloquean, reduciendo ligeramente la presión hasta que dejen de patinar y se recupere la adherencia.",
      error: 38,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "alcohol-y-conduccion",
    archivo: "alcohol",
    titulo: "¿Cuánto tarda el alcohol en hacer efecto y cuál es el límite legal?",
    descripcion:
      "El alcohol se detecta en la sangre a los 5 minutos y llega al máximo en una hora. Ley Tolerancia Cero: de 0,31 a 0,79 g/L es bajo la influencia; desde 0,8 g/L, estado de ebriedad.",
    segundos: 60,
    transcripcion: [
      "¿Cuánto tarda el alcohol en hacer efecto? Lo que dice el libro te puede sorprender.",
      "El alcohol se detecta en la sangre apenas 5 minutos después de beberlo.",
      "Y llega a su nivel máximo, en promedio, una hora después.",
      "Con igual peso y la misma cantidad de alcohol, es más probable que una mujer alcance una alcoholemia más alta que un hombre.",
      "El riesgo crece rápido: entre 0,3 y 0,5 g/L, el riesgo de siniestro se duplica; entre 0,5 y 0,8, es 5 veces mayor.",
      "La Ley Tolerancia Cero fija dos rangos. De 0,31 a 0,79 gramos es conducir bajo la influencia del alcohol, con multa y suspensión de la licencia.",
      "Desde 0,8 gramos es estado de ebriedad: multa, cancelación de la licencia y cárcel.",
      "Por eso, la única tasa segura para manejar es cero.",
      "Una de cada tres personas falla estas preguntas. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "La persona en el tránsito", ruta: "/materiales/persona-en-el-transito" },
    pregunta: {
      q: "¿Cuánto demora en alcanzar su nivel máximo el alcohol en el organismo?",
      a: "En promedio, una hora después de haber bebido. A los 5 minutos ya se detecta en la sangre.",
      error: 32,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "sistema-seguro-vision-cero",
    archivo: "sistemaseguro",
    titulo: "¿Qué es el Sistema Seguro y la Visión Cero?",
    descripcion:
      "El enfoque que sigue la Política Nacional de Seguridad de Tránsito desde 2017: las muertes en el tránsito son inaceptables, y sus cuatro principios, desde «errar es humano».",
    segundos: 59,
    transcripcion: [
      "¿Por qué la seguridad vial no depende solo del conductor? Por el enfoque Sistema Seguro.",
      "Su principio ético es que las muertes y lesiones graves en el tránsito son inaceptables. En el mundo se conoce como Visión Cero.",
      "En Chile, la Política Nacional de Seguridad de Tránsito sigue este enfoque desde 2017.",
      "Principio 1: errar es humano. Todos cometemos errores que pueden terminar en un siniestro.",
      "Principio 2: el cuerpo humano tiene una capacidad limitada para soportar la fuerza de un impacto.",
      "Principio 3: la responsabilidad es compartida entre quienes diseñan, construyen, administran y usan las vías y los vehículos.",
      "Principio 4: todos los componentes deben estar fortalecidos, para que si uno falla, los demás sigan protegiendo a las personas.",
      "La idea es diseñar un sistema que nos proteja incluso cuando alguien se equivoca.",
      "Casi cuatro de cada diez fallan estas preguntas. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Los siniestros de tránsito", ruta: "/materiales/siniestros-de-transito" },
    pregunta: {
      q: "¿Cómo se conoce mundialmente el principio ético que establece que las muertes y lesiones graves en el tránsito son inaceptables?",
      a: "Visión Cero. «Sistema Seguro» es el enfoque general; Visión Cero es el nombre de su principio ético.",
      error: 37,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "primeros-auxilios-xabcde",
    archivo: "auxilios",
    titulo: "¿Qué hacer en un choque? Deberes y primeros auxilios XABCDE",
    descripcion:
      "Detente, ayuda y avisa a la autoridad. Luego el protocolo XABCDE: hemorragias, vía aérea, respiración, circulación, conciencia y exposición. Nunca quites el casco a un motociclista.",
    segundos: 69,
    transcripcion: [
      "Si eres el primero en llegar a un choque, ¿sabes qué hacer? Primero, tus deberes.",
      "Detente, con culpa o sin ella. Enciende las luces de emergencia, apaga el motor y ayuda a las personas afectadas.",
      "Y avisa a la autoridad: indica el lugar, los vehículos y las víctimas.",
      "Para atender a un lesionado, el libro usa un protocolo de seis pasos. X: controla las hemorragias graves con presión directa y firme.",
      "A: abre la vía aérea con la maniobra frente-mentón, que despeja la lengua en una persona inconsciente.",
      "B: comprueba si respira, mirando si el pecho se eleva. C: revisa la circulación, como la piel pálida o fría, y contén las hemorragias menores.",
      "D: evalúa la conciencia: si está alerta, si responde a la voz, al dolor, o si está inconsciente. E: busca lesiones ocultas y abriga a la persona mientras llega la ayuda.",
      "Y nunca le quites el casco a un motociclista o ciclista, ni muevas a la persona, salvo peligro inminente.",
      "Cuatro de cada diez fallan estas preguntas. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Informaciones importantes", ruta: "/materiales/informaciones-importantes" },
    pregunta: {
      q: "¿A qué paso del protocolo XABCDE corresponde la maniobra frente-mentón?",
      a: "Al paso A (abrir la vía aérea): eleva el mentón para despejar la lengua, que en una persona inconsciente puede obstruir el paso del aire.",
      error: 42,
    },
    fecha: "2026-10-21",
  },
  // Videos de ~30 segundos (también en vertical para la app y YouTube Shorts).
  {
    slug: "velocidad-y-atropellos",
    archivo: "velocidad",
    titulo: "¿Cuánto cambia un atropello a 30 o a 50 km/h?",
    descripcion:
      "A 30 km/h, 9 de cada 10 peatones sobreviven a un atropello; a 45 km/h, menos de la mitad. Pasar de 30 a 50 km/h multiplica por ocho el riesgo de morir.",
    segundos: 30,
    transcripcion: [
      "¿Qué tanto cambia todo entre 30 y 50 km/h? Más de lo que crees.",
      "Si un auto atropella a un peatón a 30 km/h, nueve de cada diez sobreviven.",
      "A 45, menos de la mitad lo logra.",
      "Y a 65, lo más probable es que muera.",
      "Por eso, pasar de 30 a 50 multiplica por ocho el riesgo de morir.",
      "Casi la mitad de quienes practican falla esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Los siniestros de tránsito", ruta: "/materiales/siniestros-de-transito" },
    pregunta: {
      q: "¿En cuánto se multiplica la probabilidad de que un peatón muera atropellado cuando la velocidad del vehículo sube de 30 a 50 km/h?",
      a: "Se multiplica por ocho. A 30 km/h o menos, el 90 % de los peatones sobrevive; a 45 km/h o más, menos del 50 %; y a 65 km/h lo más probable es que el peatón fallezca.",
      error: 44,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "peatones-de-noche",
    archivo: "noche",
    titulo: "¿A qué distancia ves a un peatón de noche?",
    descripcion:
      "Con luces bajas, un peatón con ropa oscura se ve recién a 25 metros; con ropa clara, a 60; con algo reflectante, a 125. Con luces altas: 150, 300 y 450 metros.",
    segundos: 30,
    transcripcion: [
      "De noche, con luces bajas, ¿a qué distancia alcanzas a ver a un peatón? Depende de su ropa.",
      "Si viste ropa oscura, lo ves recién a 25 metros.",
      "Con ropa clara, a unos 60.",
      "Y con algo reflectante, a 125 metros: cinco veces antes.",
      "Con luces altas, esas distancias llegan a 150, 300 y 450 metros.",
      "Cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Conducción en circunstancias especiales", ruta: "/materiales/circunstancias-especiales" },
    pregunta: {
      q: "Al conducir de noche con luces bajas, ¿a qué distancia puede ver a un peatón con ropa reflectante?",
      a: "A unos 125 metros. Con luces bajas, la ropa oscura se ve a unos 25 metros y la ropa clara, a unos 60.",
      error: 42,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "profundidad-neumaticos",
    archivo: "neumaticos",
    titulo: "¿Cuándo dejan de ser seguros tus neumáticos?",
    descripcion:
      "Los surcos deben tener al menos 3 mm de profundidad; bajo 1,6 mm, con lluvia, el neumático puede perder el contacto con el pavimento. La presión se mide en frío, cada dos semanas.",
    segundos: 28,
    transcripcion: [
      "Tus neumáticos son lo único que toca el suelo. ¿Sabes cuándo dejan de ser seguros?",
      "El libro aconseja que los surcos tengan al menos 3 milímetros de profundidad.",
      "Bajo 1,6 milímetros, con lluvia, el neumático puede perder el contacto con el pavimento.",
      "Y la presión se mide en frío: al rodar, el aire se calienta y marca de más.",
      "Revísala al menos cada dos semanas.",
      "Casi la mitad falla esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Los principios de la conducción", ruta: "/materiales/principios-de-la-conduccion" },
    pregunta: {
      q: "¿Cuál es la profundidad mínima aconsejable de los dibujos o surcos de la banda de rodamiento de los neumáticos?",
      a: "3 mm. Bajo 1,6 mm empeora la fricción con el pavimento mojado y aumenta el riesgo de perder el contacto con la calzada.",
      error: 47,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "cinturon-de-seguridad",
    archivo: "cinturon",
    titulo: "¿Cuánto aumenta el riesgo si no usas cinturón?",
    descripcion:
      "Si sales despedido del auto por no usar cinturón, la probabilidad de una lesión medular aumenta en un 1.300 % y la de morir, en un 300 %. Atrás es obligatorio desde 2002.",
    segundos: 29,
    transcripcion: [
      "Si no usas cinturón y sales despedido del auto en un choque, ¿cuánto aumenta el riesgo?",
      "La probabilidad de sufrir una lesión medular aumenta en un 1.300 %.",
      "Y la de morir, en un 300 %.",
      "Atrás también cuenta: en autos fabricados desde el año 2002, el cinturón trasero es obligatorio.",
      "Abróchalo siempre, aunque el viaje sea corto.",
      "Cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Los principios de la conducción", ruta: "/materiales/principios-de-la-conduccion" },
    pregunta: {
      q: "Si una persona sale eyectada del vehículo durante un siniestro por no usar cinturón, ¿en cuánto aumenta su probabilidad de sufrir una lesión medular?",
      a: "Aumenta en un 1.300 %, y la de resultar fallecida, en un 300 %.",
      error: 43,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "distraccion-y-fatiga",
    archivo: "fatiga",
    titulo: "¿Cuánto avanza tu auto en un segundo de distracción?",
    descripcion:
      "A 90 km/h, un estornudo de un segundo equivale a unos 25 metros sin mirar la vía. En viajes largos, descansa cada dos horas o 200 km; si te da sueño, detente.",
    segundos: 35,
    transcripcion: [
      "Un estornudo dura un segundo. ¿Cuánto avanza tu auto mientras no miras?",
      "A 90 km/h, unos 25 metros sin prestar atención a la vía.",
      "Con el cansancio pasa algo peor: los microsueños, segundos en que te duermes sin darte cuenta.",
      "Por eso, en viajes largos, descansa cada dos horas o cada 200 kilómetros.",
      "Y si te da sueño, detente: 20 a 30 minutos de sueño suelen bastar.",
      "Casi cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "La persona en el tránsito", ruta: "/materiales/persona-en-el-transito" },
    pregunta: {
      q: "En un viaje por carretera a 90 km/h, ¿cuántos metros recorre el vehículo durante un estornudo de 1 segundo de duración sin que se pueda prestar atención a la vía?",
      a: "Aproximadamente 25 metros.",
      error: 38,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "limites-de-velocidad",
    archivo: "velocidades",
    titulo: "¿Cuál es la velocidad máxima en cada tipo de vía en Chile?",
    descripcion:
      "En zona urbana, 50 km/h. En carretera, 100 km/h con una pista por sentido y 120 km/h con dos o más (vehículos livianos). Buses, camiones y escolares: 90 km/h.",
    segundos: 28,
    transcripcion: [
      "¿Sabes cuál es la velocidad máxima en cada tipo de vía? Muchos se equivocan.",
      "En zonas urbanas, el límite general es de 50 km/h.",
      "En carretera con una sola pista por sentido, 100 para vehículos livianos.",
      "Con dos o más pistas en el mismo sentido, sube a 120.",
      "Y buses, camiones y transporte escolar no deben pasar de 90.",
      "Casi cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Normas de circulación", ruta: "/materiales/normas-de-circulacion" },
    pregunta: {
      q: "¿Cuál es la velocidad máxima permitida en zonas no urbanas para vehículos livianos en una pista por sentido?",
      a: "100 km/h. Cuando hay dos o más pistas en un mismo sentido, el límite es 120 km/h.",
      error: 37,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "como-estacionar",
    archivo: "estacionar",
    titulo: "¿Cómo se estaciona correctamente, también en pendiente?",
    descripcion:
      "Al lado derecho, a no más de 30 cm de la cuneta y a 60 cm de otros vehículos. En subida, ruedas hacia el centro de la calzada; en bajada, hacia la cuneta.",
    segundos: 27,
    transcripcion: [
      "Estacionar bien también se evalúa en el examen. ¿Recuerdas estas reglas?",
      "Estaciona al lado derecho, a no más de 30 centímetros de la cuneta.",
      "Y deja al menos 60 centímetros con los otros vehículos.",
      "En una subida, deja las ruedas giradas hacia el centro de la calzada.",
      "En una bajada, al revés: hacia la cuneta.",
      "Casi cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Normas de circulación", ruta: "/materiales/normas-de-circulacion" },
    pregunta: {
      q: "¿Cómo se deben dejar las ruedas al estacionar en una subida?",
      a: "Giradas hacia el centro de la calzada, para que, si el vehículo se desplaza, se frene contra la vía. En bajada, hacia la cuneta.",
      error: 39,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "ninos-y-personas-mayores",
    archivo: "vulnerables",
    titulo: "¿Por qué hay que cuidar más a niños y personas mayores en el tránsito?",
    descripcion:
      "La vista de los niños no se desarrolla del todo hasta los 15 años y actúan con seguridad recién entre los 9 y 12. Una persona mayor puede necesitar 4 segundos más para cruzar.",
    segundos: 33,
    transcripcion: [
      "Niños y personas mayores necesitan que los conductores los cuiden más. ¿Sabes por qué?",
      "La vista de niñas y niños no se desarrolla completamente hasta los 15 años.",
      "Y recién entre los 9 y los 12 años empiezan a actuar con seguridad en el tránsito.",
      "Una persona mayor puede necesitar hasta 4 segundos más para cruzar una calle de 16 metros.",
      "Cerca de colegios y cruces, baja la velocidad y espera con paciencia.",
      "Cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Las y los usuarios vulnerables", ruta: "/materiales/usuarios-vulnerables" },
    pregunta: {
      q: "¿Hasta qué edad NO se desarrolla completamente la vista de los niños y niñas?",
      a: "Hasta los 15 años de edad.",
      error: 42,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "coletazo-y-perdida-de-agarre",
    archivo: "coletazo",
    titulo: "¿Qué hacer si tu auto pierde agarre o da un coletazo?",
    descripcion:
      "Con tracción delantera, deja de acelerar y retoma la trayectoria. Con tracción trasera, ante un coletazo, sigue acelerando y gira hacia donde quieres ir. Nunca frenes de golpe.",
    segundos: 28,
    transcripcion: [
      "Si tu auto pierde agarre y se desliza, ¿aceleras o sueltas el pie? Depende de la tracción.",
      "Con tracción delantera, si las ruedas resbalan, deja de acelerar y retoma la trayectoria.",
      "Con tracción trasera, ante un coletazo, sigue acelerando y gira la dirección hacia donde quieres ir.",
      "En ningún caso frenes de golpe ni sueltes el volante.",
      "Cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Los principios de la conducción", ruta: "/materiales/principios-de-la-conduccion" },
    pregunta: {
      q: "En un vehículo con tracción trasera, si las ruedas pierden fricción y se produce un coletazo, ¿qué se debe hacer?",
      a: "Continuar acelerando y girar la dirección hacia la trayectoria deseada. Frenar bruscamente o soltar el volante no permite recuperar el control.",
      error: 40,
    },
    fecha: "2026-10-21",
  },
  {
    slug: "cruce-ferroviario",
    archivo: "ferroviario",
    titulo: "¿Cuánto necesita un tren para frenar y cómo cruzar la vía férrea?",
    descripcion:
      "A 100 km/h, un tren necesita entre 800 y 1.000 metros para detenerse. Detente, mira y escucha; no adelantes a menos de 200 metros del cruce ni estaciones a menos de 20.",
    segundos: 31,
    transcripcion: [
      "Un tren no frena como un auto. A 100 km/h, ¿cuánto necesita para detenerse?",
      "Entre 800 y 1.000 metros. Por eso el tren siempre tiene la preferencia.",
      "Antes de cruzar, detente, mira a ambos lados y escucha.",
      "No adelantes a menos de 200 metros del cruce, ni estaciones a menos de 20.",
      "Y si ya empezaste a cruzar y se activan las señales, no te detengas.",
      "Cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Normas de circulación", ruta: "/materiales/normas-de-circulacion" },
    pregunta: {
      q: "¿A qué distancia de un cruce ferroviario está prohibido adelantar a otro vehículo?",
      a: "A menos de 200 metros. Tampoco se puede estacionar a menos de 20 metros del cruce.",
      error: 41,
    },
    fecha: "2026-10-21",
  },
  // Lote 2026-10-30: preguntas más falladas (fuente: documentos/preguntas-mas-falladas-2026-09-30.csv).
  {
    slug: "sobrepasar-adelantar-estacionar-detenerse",
    archivo: "definiciones",
    titulo: "¿Sobrepasar o adelantar? ¿Estacionar o detenerse? Las diferencias",
    descripcion:
      "Sobrepasar es pasar a otro sin cruzar el eje de la calzada; adelantar, hacerlo por el lado izquierdo del eje. Detenerse es una parada breve; estacionar, quedarse más que eso.",
    segundos: 38,
    transcripcion: [
      "Sobrepasar y adelantar no son lo mismo. Y estacionar tampoco es lo mismo que detenerse.",
      "Sobrepasar es pasar a otro vehículo que va en tu mismo sentido, sin cruzar el eje de la calzada.",
      "Adelantar, en cambio, es ponerte delante de otro por el lado izquierdo del eje.",
      "Detenerse es parar brevemente: por un semáforo, por orden de Carabineros, o para subir o bajar pasajeros.",
      "Si te quedas más tiempo que eso, con o sin la persona que conduce adentro, ya estás estacionando.",
      "Uno de cada tres falla estas preguntas. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Glosario vial", ruta: "/materiales/glosario-vial" },
    pregunta: {
      q: "Sobrepasar es:",
      a: "Pasar por otro u otros vehículos en el mismo sentido, sin traspasar el eje de la calzada.",
      error: 34,
    },
    fecha: "2026-10-30",
  },
  {
    slug: "remolque-peso-sobre-la-esfera",
    archivo: "remolque",
    titulo: "¿Qué pasa si el remolque carga poco o mucho sobre la bola de arrastre?",
    descripcion:
      "Con poca presión sobre la esfera, la cola del auto se levanta y el conjunto serpentea; con demasiada, se hunde y las luces bajas encandilan. Cómo cargar bien el remolque.",
    segundos: 39,
    transcripcion: [
      "¿Llevas remolque? El peso sobre la bola de arrastre cambia cómo se comporta tu auto.",
      "Si la presión sobre la esfera es baja o nula, la parte trasera del auto se levanta.",
      "Las ruedas traseras pierden presión, todo el conjunto empieza a serpentear y el remolque puede volcarse.",
      "Si es excesiva, la parte trasera se hunde, las ruedas delanteras pierden presión y tus luces bajas encandilan a quienes vienen de frente.",
      "Distribuye bien la carga y sigue las recomendaciones del fabricante.",
      "Casi siete de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Conducción en circunstancias especiales", ruta: "/materiales/circunstancias-especiales" },
    pregunta: {
      q: "¿Qué sucede cuando la presión del remolque sobre la esfera de arrastre es muy baja o nula?",
      a: "Se eleva la parte trasera del automóvil, disminuye la presión en las ruedas traseras y el vehículo puede serpentear.",
      error: 69,
    },
    fecha: "2026-10-30",
  },
  {
    slug: "adelantar-por-la-derecha",
    archivo: "derecha",
    titulo: "¿Cuándo se puede adelantar por la derecha?",
    descripcion:
      "Solo en dos casos: cuando el vehículo de adelante vira o va a virar a la izquierda, y en vías urbanas con 3 o más pistas en el mismo sentido. Siempre sin salir de la calzada.",
    segundos: 30,
    transcripcion: [
      "¿Se puede adelantar por la derecha? Casi nunca, pero hay dos excepciones.",
      "La regla general: siempre se adelanta por la izquierda.",
      "Primera excepción: cuando el vehículo de adelante está virando, o a punto de virar, a la izquierda.",
      "Segunda: en vías urbanas con 3 o más pistas en el mismo sentido.",
      "En ambos casos, con seguridad y sin salirte de la calzada: nunca por la berma.",
      "Uno de cada tres falla esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Normas de circulación", ruta: "/materiales/normas-de-circulacion" },
    pregunta: {
      q: "¿En qué caso está permitido adelantar a otro vehículo por la derecha?",
      a: "Cuando el vehículo de adelante ha señalado su intención de girar a la izquierda y existe espacio suficiente.",
      error: 34,
    },
    fecha: "2026-10-30",
  },
  {
    slug: "tachas-y-lineas-de-pista",
    archivo: "tachas",
    titulo: "¿Qué significan las tachas blancas y rojas en el pavimento?",
    descripcion:
      "Tachas blancas con línea segmentada: puedes cambiar de pista señalizando. Rojas con línea continua: no se traspasa. Líneas mixtas y por qué las tachas se ven con lluvia.",
    segundos: 35,
    transcripcion: [
      "Esos ojos de gato en el pavimento te dicen si puedes cambiar de pista.",
      "Las tachas blancas acompañan líneas segmentadas: puedes cruzarlas, señalizando antes.",
      "Las rojas van con líneas continuas: no hay condiciones seguras para traspasarlas, como antes de una curva o un cambio de rasante.",
      "Si la línea es mixta, solo puede cruzarla quien tiene el lado segmentado.",
      "Y de noche o con lluvia, las tachas se siguen viendo cuando la pintura ya no.",
      "Casi cuatro de cada diez fallan esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Normas de circulación", ruta: "/materiales/normas-de-circulacion" },
    pregunta: {
      q: "¿Qué indica al conductor encontrar únicamente tachas blancas entre las líneas de pista?",
      a: "Que los carriles adyacentes circulan en el mismo sentido y el cambio de pista está permitido.",
      error: 37,
    },
    fecha: "2026-10-30",
  },
  {
    slug: "zona-de-transito-calmado",
    archivo: "calmado",
    titulo: "¿Qué velocidad máxima tiene una zona de tránsito calmado?",
    descripcion:
      "Son vías urbanas con un límite menor al de la ley, que puede ser 40, 30 o 20 km/h según lo fije la autoridad. Al entrar, respeta lo que indiquen las señales.",
    segundos: 28,
    transcripcion: [
      "¿Qué velocidad máxima tiene una zona de tránsito calmado? No siempre es 30.",
      "Son calles urbanas donde la autoridad fija un límite menor que el de la ley.",
      "Ese límite puede ser 40, 30 o 20 km/h.",
      "Se logra con señales o con cambios en la propia calle, que obligan a ir más lento.",
      "Al entrar, baja a la velocidad que indiquen las señales.",
      "Uno de cada tres falla esta pregunta. Practica en Escuela Vial.",
    ],
    capitulo: { nombre: "Glosario vial", ruta: "/materiales/glosario-vial" },
    pregunta: {
      q: "¿Qué velocidades máximas puede establecer una zona de tránsito calmado?",
      a: "20, 30 o 40 kilómetros por hora, según establezca la autoridad para esa área.",
      error: 34,
    },
    fecha: "2026-10-30",
  },
];

export const getVideo = (slug: string) => VIDEOS.find((v) => v.slug === slug);
export const videosDeCapitulo = (ruta: string) => VIDEOS.filter((v) => v.capitulo.ruta === ruta);

/** MP4 para la web (horizontal), con calidad automática de Cloudinary. */
export const videoSrc = (v: Video) => `${CLOUD}/q_auto/escuela-vial/video/${v.archivo}-horizontal.mp4`;
/** URL original del archivo (para schema y sitemap). */
export const videoContentUrl = (v: Video) => `${CLOUD}/escuela-vial/video/${v.archivo}-horizontal.mp4`;
/** Miniatura generada por Cloudinary a los 2 s (escena de apertura con el título). */
export const videoPoster = (v: Video, ancho = 1280) => `${CLOUD}/so_2,w_${ancho},q_auto/escuela-vial/video/${v.archivo}-horizontal.jpg`;
/** Duración para mostrar: "0:30", "1:12". */
export const videoDuracionTexto = (v: Video) => `${Math.floor(v.segundos / 60)}:${String(v.segundos % 60).padStart(2, "0")}`;
/** Duración ISO 8601 (PT30S) para schema.org. */
export const videoDuracionIso = (v: Video) => `PT${v.segundos}S`;
